import { useEffect, useMemo } from 'react';
import { API, APIProps } from '@stoplight/elements';
import { useRouter } from 'nextra/hooks';
import '@stoplight/elements/styles.min.css';

export type ApiDocsProps = Omit<APIProps, 'router'> & {
  apiDescriptionUrl: string;
};

/**
 * A component that uses Stoplight to render API documentation based on a Swagger definition file.
 */
export function ApiDocs({ basePath, ...props }: ApiDocsProps) {
  const router = useRouter();

  const apiBasePath = useMemo(() => {
    if (basePath !== undefined) {
      return basePath;
    }

    // If basePath wasn't specified, use the route's path without the last (probably dynamic) segment
    const pathSegments = router.pathname.split('/');
    pathSegments.splice(pathSegments.length - 1, 1);
    return pathSegments.join('/');
  }, [basePath, router.pathname]);

  // Use the 'static' router from stoplight for server-side rendering
  const apiRouter = typeof window === 'undefined' ? 'static' : 'history';

  // NB(sc-32402): Elements adds an unwanted prefix to all href's in our
  // Swagger spec which breaks our link to the raw Swagger JSON.
  // In addition, our Swagger specs reference a tenant-specific link which is not available
  // in the standalone site, so we replace the link with one based on the apiDescriptionUrl.
  const replaceURL = props.apiDescriptionUrl.replace('_DOCUMENTATION_ONLY', '');
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const anchors = document.querySelectorAll('a[href$="swagger.json"]');
      if (anchors.length) {
        anchors.forEach((a) => {
          const href = a.getAttribute('href');
          if (href && href != replaceURL) {
            const fixed = a.cloneNode(true) as HTMLAnchorElement;
            fixed.href = replaceURL;
            fixed.text = 'Download';
            a.replaceWith(fixed);
          }
        });
      }
    });

    // Select on the entire body to avoid racing against <elements-api> being
    // added.
    observer.observe(document.querySelector('body') as HTMLElement, {
      childList: true,
      subtree: true,
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="chrono-api-docs">
      <API hideSchemas {...props} router={apiRouter} basePath={apiBasePath} />
    </div>
  );
}
